/**
 * CPApprovalHistoryDrawer — chronological, filterable audit trail of
 * every Competent Person sign-off for a project.
 *
 * Sprint P37o · Feb 2026. Data source: `/api/projects/{pid}/cp-approval/audit`
 * (immutable, append-only). CSV export via `/audit.csv`.
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  CaretDown, CaretRight, ClockCounterClockwise, Copy, Download,
  FileText, Fingerprint, Funnel, MagnifyingGlass, ShieldCheck, X,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";

const STATUS_STYLE = {
  FINAL: "bg-emerald-50 border-emerald-200 text-emerald-700",
  DRAFT: "bg-blue-50 border-blue-200 text-blue-700",
};

export default function CPApprovalHistoryDrawer({ open, onClose, projectId, project }) {
  const [statusFilter,   setStatusFilter]   = useState("");
  const [cpFilter,       setCpFilter]       = useState("");
  const [versionFilter,  setVersionFilter]  = useState("");
  const [q,              setQ]              = useState("");
  const [expanded,       setExpanded]       = useState(new Set());

  const params = useMemo(() => {
    const p = {};
    if (statusFilter)  p.status = statusFilter;
    if (cpFilter)      p.cp = cpFilter;
    if (versionFilter) p.report_version = versionFilter;
    if (q)             p.q = q;
    return p;
  }, [statusFilter, cpFilter, versionFilter, q]);

  const auditQ = useQuery({
    queryKey: ["cp-audit", projectId, params],
    queryFn: () => api.get(`/projects/${projectId}/cp-approval/audit`, { params }).then((r) => r.data),
    enabled: !!projectId && !!open,
    staleTime: 15_000,
  });

  const rows = useMemo(() => auditQ.data?.audit || [], [auditQ.data]);
  const cps  = useMemo(() =>
    Array.from(new Set(rows.map((r) => r.cp_name).filter(Boolean))).sort(),
    [rows]);
  const versions = useMemo(() =>
    Array.from(new Set(rows.map((r) => r.report_version).filter(Boolean))).sort(),
    [rows]);

  const toggle = (id) => {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpanded(next);
  };

  const copyHash = async (h) => {
    try { await navigator.clipboard.writeText(h); toast.success("Signature hash copied."); }
    catch { toast.error("Copy failed"); }
  };

  const downloadCSV = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}/cp-approval/audit.csv`;
    const a = document.createElement("a"); a.href = url;
    a.setAttribute("download", `cp-approval-audit-${String(projectId).slice(0,8)}.csv`);
    document.body.appendChild(a); a.click(); a.remove();
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="cp-history-drawer"
      className="fixed inset-0 z-50 bg-slate-950/40 backdrop-blur-[2px]"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="absolute right-0 top-0 h-full w-full max-w-4xl bg-white shadow-2xl flex flex-col">
        <div className="px-6 py-4 border-b border-slate-200 flex items-start justify-between gap-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500">
              Immutable audit trail
            </div>
            <div className="mt-1 flex items-center gap-2">
              <ClockCounterClockwise size={18} className="text-brand-blue" />
              <h2 className="font-heading font-black text-2xl tracking-tight">CP Approval History</h2>
            </div>
            <p className="mt-1 text-[12px] text-slate-500">
              Every recorded Competent Person sign-off for <b>{project?.name || projectId}</b>.
              Records are append-only.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={downloadCSV}
                       className="gap-1.5" data-testid="cp-history-csv">
              <Download size={12} /> CSV
            </Button>
            <Button variant="ghost" size="sm" onClick={onClose}
                       className="gap-1.5" data-testid="cp-history-close">
              <X size={14} /> Close
            </Button>
          </div>
        </div>

        <div className="px-6 py-3 border-b border-slate-100 bg-slate-50 flex flex-wrap items-center gap-2">
          <div className="relative">
            <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)}
                     placeholder="Search CP, email, notes…" data-testid="cp-history-search"
                     className="pl-7 h-8 w-64 text-[12px]" />
          </div>
          <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-28 h-8 text-[12px]" data-testid="cp-history-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="FINAL">FINAL</SelectItem>
              <SelectItem value="DRAFT">DRAFT</SelectItem>
            </SelectContent>
          </Select>
          <Select value={cpFilter || "all"} onValueChange={(v) => setCpFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-56 h-8 text-[12px]" data-testid="cp-history-cp"><SelectValue placeholder="Competent Person" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All CPs</SelectItem>
              {cps.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
            </SelectContent>
          </Select>
          <Select value={versionFilter || "all"} onValueChange={(v) => setVersionFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-40 h-8 text-[12px]" data-testid="cp-history-version"><SelectValue placeholder="Report version" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All versions</SelectItem>
              {versions.map((v) => (<SelectItem key={v} value={v}>{v}</SelectItem>))}
            </SelectContent>
          </Select>
          {(statusFilter || cpFilter || versionFilter || q) && (
            <Button variant="ghost" size="sm" onClick={() => {
              setStatusFilter(""); setCpFilter(""); setVersionFilter(""); setQ("");
            }} data-testid="cp-history-clear">
              <Funnel size={11} className="mr-1" /> Clear filters
            </Button>
          )}
          <div className="ml-auto text-[11px] text-slate-500">
            {auditQ.isLoading ? "loading…" : `${rows.length} record${rows.length === 1 ? "" : "s"}`}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {auditQ.isLoading ? (
            <div className="animate-pulse space-y-3" data-testid="cp-history-loading">
              {Array.from({length: 6}).map((_, i) => (
                <div key={i} className="h-16 bg-slate-100 rounded-md" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-8 text-center text-slate-500"
                    data-testid="cp-history-empty">
              <ShieldCheck size={22} className="mx-auto mb-2 text-slate-300" />
              No CP approvals recorded for this project.
              <div className="mt-1 text-[11px]">Approvals will appear here after a Competent Person signs off.</div>
            </div>
          ) : (
            <ol className="relative pl-4 border-l-2 border-slate-100 space-y-3">
              {rows.map((r, idx) => {
                const isExp = expanded.has(r.id || idx);
                const st = r.approval_status || "DRAFT";
                return (
                  <li key={r.id || idx} className="relative" data-testid={`cp-history-row-${idx}`}>
                    <span className={`absolute -left-[9px] top-4 h-3 w-3 rounded-full border-2 border-white ${st === "FINAL" ? "bg-emerald-500" : "bg-blue-500"}`} />
                    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
                      <button
                        onClick={() => toggle(r.id || idx)}
                        className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-slate-50"
                        data-testid={`cp-history-toggle-${idx}`}>
                        {isExp ? <CaretDown size={12} /> : <CaretRight size={12} />}
                        <div className="flex flex-col flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 min-w-0">
                            <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${STATUS_STYLE[st] || STATUS_STYLE.DRAFT}`}>{st}</span>
                            <span className="font-semibold text-slate-800 truncate">{r.cp_name || "—"}</span>
                            <span className="text-[11px] text-slate-500 font-mono">{r.report_version || ""}</span>
                          </div>
                          <div className="text-[11px] text-slate-500 mt-0.5 truncate">
                            {(r.approved_at || "").slice(0,19).replace("T", " ")}
                            {r.approved_by_email && <> · {r.approved_by_email}</>}
                            {r.reporting_standard && <> · {r.reporting_standard}</>}
                          </div>
                        </div>
                      </button>
                      {isExp && (
                        <div className="border-t border-slate-100 bg-slate-50/70 px-4 py-3 grid grid-cols-1 md:grid-cols-2 gap-2 text-[12px]">
                          <KV k="CP registration" v={r.cp_registration} />
                          <KV k="Membership" v={r.cp_membership} />
                          <KV k="Effective date" v={r.effective_date} />
                          <KV k="Density (t/m³)" v={r.density_default != null ? Number(r.density_default).toFixed(3) : null} />
                          <KV k="Density source" v={r.density_source} />
                          <KV k="Classification approved" v={r.classification_approved ? "Yes" : "No"} />
                          <KV k="Publication Gate" v={r.publication_gate_status} />
                          <KV k="Blockers" v={(r.publication_blockers || []).join(", ")} full />
                          <KV k="Request ID" v={r.request_id} />
                          <KV k="IP" v={r.approved_by_ip} />
                          <KV k="Notes" v={r.notes} full />
                          <div className="md:col-span-2 mt-1 pt-2 border-t border-slate-100 flex items-center gap-2 flex-wrap">
                            <Fingerprint size={13} className="text-slate-400" />
                            <span className="text-[10px] uppercase tracking-widest text-slate-500">Signature hash</span>
                            <span className="font-mono text-[10.5px] text-slate-700 truncate break-all">
                              {r.signature_hash || "—"}
                            </span>
                            {r.signature_hash && (
                              <button onClick={() => copyHash(r.signature_hash)}
                                        className="ml-auto inline-flex items-center gap-1 h-6 px-1.5 rounded border border-slate-300 bg-white text-slate-600 text-[10.5px] hover:bg-slate-100"
                                        data-testid={`cp-history-copy-hash-${idx}`}>
                                <Copy size={10} /> Copy
                              </button>
                            )}
                          </div>
                          <div className="md:col-span-2 mt-2 pt-2 border-t border-slate-100 flex flex-wrap gap-1">
                            <PhaseBadge on={r.phase_data_reviewed}          label="Data" />
                            <PhaseBadge on={r.phase_gis_reviewed}           label="GIS" />
                            <PhaseBadge on={r.phase_estimation_reviewed}    label="Estimation" />
                            <PhaseBadge on={r.phase_classification_reviewed} label="Classification" />
                            <PhaseBadge on={r.phase_report_reviewed}         label="Report" />
                            <PhaseBadge on={r.phase_final_signoff}            label="Final sign-off" />
                          </div>
                          {r.report_version && (
                            <div className="md:col-span-2 mt-2 text-[11px] text-slate-500 inline-flex items-center gap-1.5">
                              <FileText size={11} /> Report version <span className="font-mono">{r.report_version}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}

function KV({ k, v, full }) {
  if (v == null || v === "") return null;
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{k}</div>
      <div className="text-[12.5px] text-slate-800 break-all">{String(v)}</div>
    </div>
  );
}
function PhaseBadge({ on, label }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10.5px] ${on
      ? "bg-emerald-50 border-emerald-200 text-emerald-700"
      : "bg-slate-50 border-slate-200 text-slate-400"}`}>
      {on ? "✓" : "○"} {label}
    </span>
  );
}
